import { useEffect } from 'react'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { cn } from '@/lib/cn'

/**
 * Sets `document.title`. Pages call this only for titles the router cannot know
 * (a site's install name, say) — `App` already titles every static route, and a
 * child effect always lands after the router's render-phase default.
 */
export function useDocumentTitle(title: string): void {
  useEffect(() => {
    if (!title) return
    document.title = `${title} · Argus`
  }, [title])
}

export interface PageHeaderProps {
  title: ReactNode
  description?: ReactNode
  /** Buttons for this page. Wraps below the title on narrow viewports. */
  actions?: ReactNode
  /** Small label above the title, e.g. a parent entity. */
  eyebrow?: ReactNode
  backTo?: string
  backLabel?: string
  /** Overrides the `document.title` derived from `title`. */
  documentTitle?: string
  className?: string
  /** Extra row rendered under the header — a toolbar or filter strip. */
  children?: ReactNode
}

export default function PageHeader({
  title,
  description,
  actions,
  eyebrow,
  backTo,
  backLabel,
  documentTitle,
  className,
  children
}: PageHeaderProps): React.ReactElement {
  useDocumentTitle(documentTitle ?? (typeof title === 'string' ? title : ''))

  return (
    <header className={cn('mb-6 flex flex-col gap-4', className)}>
      {backTo && (
        <Link
          to={backTo}
          className="-ml-1 inline-flex w-fit items-center gap-1 rounded-control px-1 py-0.5 text-sm font-medium text-fg-muted transition-colors hover:text-fg"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          {backLabel ?? 'Back'}
        </Link>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
        <div className="min-w-0">
          {eyebrow && (
            <p className="mb-1 text-[11px] font-semibold tracking-[0.08em] text-fg-subtle uppercase">
              {eyebrow}
            </p>
          )}
          <h1 className="truncate text-xl font-semibold tracking-tight text-fg sm:text-2xl">
            {title}
          </h1>
          {description && (
            <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-fg-muted">{description}</p>
          )}
        </div>

        {actions && (
          <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">{actions}</div>
        )}
      </div>

      {children}
    </header>
  )
}

export { PageHeader }
